import React, { useEffect, useMemo, useState } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { normalizeDisplayPowerUnit } from '../utils/displayPowerUnit';
import './EnergyFlow3D.css';

/**
 * Diagramë rrjedhje energjie — dizajni nga loadi-main (EnergyFlow3D).
 * Props të njëjta si më parë: kW / MW nga buildPowerFlowDiagramValues.
 */
export default function EnergyPowerFlowDiagram({
  ppvKw = 0,
  batteryKw = 0,
  loadKw = 0,
  gridImportKw = 0,
  /** Pt < 0 → eksport në kW (pozitiv); kur > 0, shfaqet etiketa Eksport në vend të Import */
  gridExportKw = 0,
  displayPowerUnit,
  isDark = false,
  className = '',
  /** 0–100 për unazën e baterisë; nëse mungon → 100 */
  batterySocPercent,
}) {
  const { t } = useLanguage();
  const unit = normalizeDisplayPowerUnit(displayPowerUnit);

  const fmt = (kw) => {
    const n = Number(kw);
    const value = Number.isFinite(n) ? Math.abs(n) : 0;
    if (unit === 'kw') {
      const rounded = Math.round(value * 100) / 100;
      return `${Number.isInteger(rounded) ? rounded.toFixed(0) : rounded.toFixed(2)}${t('emPowerFlowUnitKw')}`;
    }
    const rounded = Math.round((value / 1000) * 1000) / 1000;
    return `${Number.isInteger(rounded) ? rounded.toFixed(0) : rounded.toFixed(3)}${t('emPowerFlowUnitMw')}`;
  };

  const gridFlow = useMemo(() => {
    const exportKw = Number(gridExportKw);
    const importKw = Number(gridImportKw);
    if (Number.isFinite(exportKw) && exportKw > 1e-9) {
      return { kw: exportKw, isExport: true };
    }
    if (Number.isFinite(importKw) && importKw < -1e-9) {
      return { kw: Math.abs(importKw), isExport: true };
    }
    return {
      kw: Number.isFinite(importKw) ? Math.max(0, importKw) : 0,
      isExport: false,
    };
  }, [gridExportKw, gridImportKw]);

  const ppv = fmt(ppvKw);
  const discharge = fmt(batteryKw);
  const load = fmt(loadKw);
  const gridPower = fmt(gridFlow.kw);
  const gridLabel = gridFlow.isExport ? t('chintCardExport') : t('emPowerFlowGridImport');

  const hasFlow = (kw) => Number(kw) > 1e-9;
  const flows = {
    solar: hasFlow(ppvKw),
    battery: hasFlow(batteryKw),
    load: hasFlow(loadKw),
    grid: hasFlow(gridFlow.kw),
  };

  const ringLevel =
    batterySocPercent != null && Number.isFinite(Number(batterySocPercent))
      ? Math.max(0, Math.min(100, Number(batterySocPercent)))
      : 100;

  const [tilt, setTilt] = useState({ x: 0, y: 0 });
  useEffect(() => {
    const onMove = (e) => {
      const te = e.touches ? e.touches[0] : e;
      const w = window.innerWidth;
      const h = window.innerHeight;
      setTilt({
        x: ((te.clientY - h / 2) / h) * 10,
        y: ((te.clientX - w / 2) / w) * -10,
      });
    };
    window.addEventListener('pointermove', onMove, { passive: true });
    window.addEventListener('touchmove', onMove, { passive: true });
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('touchmove', onMove);
    };
  }, []);

  const particles = useMemo(() => Array.from({ length: 5 }, (_, i) => i), []);

  const rootClass = [
    'ef3d-root',
    'ef3d-fullwidth',
    isDark ? '' : 'ef3d-light',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={rootClass} data-testid="energy-power-flow" role="img" aria-label={t('emPowerFlowTitle')}>
      <div className="ef3d-ambient" aria-hidden />
      <div className="ef3d-grain" aria-hidden />

      <div
        className="ef3d-stage"
        style={{
          transform: `rotateX(${tilt.x}deg) rotateY(${tilt.y}deg)`,
        }}
      >
        <div className="ef3d-orbit ef3d-orbit-1" />
        <div className="ef3d-orbit ef3d-orbit-2" />
        <div className="ef3d-orbit ef3d-orbit-3" />

        {/* Solar */}
        <div className="ef3d-node ef3d-node-top">
          <div className="ef3d-label ef3d-label-top">
            <span className="ef3d-label-title">{t('emPowerFlowPpv')}</span>
            <span className="ef3d-label-value ef3d-val-solar">{ppv}</span>
          </div>
          <div className="ef3d-icon-wrap ef3d-icon-solar">
            <div className="ef3d-icon-inner">
              <SolarIcon />
            </div>
            <div className="ef3d-icon-glow ef3d-glow-solar" />
          </div>
        </div>

        {/* Battery */}
        <div className="ef3d-node ef3d-node-left">
          <div className="ef3d-icon-wrap ef3d-icon-battery">
            <svg className="ef3d-ring" viewBox="0 0 100 100">
              <circle className="ef3d-ring-bg" cx="50" cy="50" r="46" />
              <circle
                className="ef3d-ring-fg ef3d-ring-green"
                cx="50"
                cy="50"
                r="46"
                style={{
                  strokeDasharray: 289,
                  strokeDashoffset: 289 - (289 * ringLevel) / 100,
                }}
              />
            </svg>
            <div className="ef3d-icon-inner">
              <BatteryIcon />
            </div>
            <div className="ef3d-icon-glow ef3d-glow-green" />
          </div>
          <div className="ef3d-label ef3d-label-left">
            <span className="ef3d-label-title">{t('emPowerFlowBattery')}</span>
            <span className="ef3d-label-value ef3d-val-battery">{discharge}</span>
          </div>
        </div>

        {/* Inverter */}
        <div className="ef3d-node ef3d-node-center">
          <div className="ef3d-core">
            <div className="ef3d-core-ring" />
            <div className="ef3d-core-ring ef3d-core-ring-2" />
            <div className="ef3d-core-ring ef3d-core-ring-3" />
            <div className="ef3d-core-sphere">
              <InverterIcon />
            </div>
            <div className="ef3d-core-pulse" />
          </div>
        </div>

        {/* Load */}
        <div className="ef3d-node ef3d-node-right">
          <div className="ef3d-icon-wrap ef3d-icon-house">
            <div className="ef3d-icon-inner">
              <HouseIcon />
            </div>
            <div className="ef3d-icon-glow ef3d-glow-house" />
          </div>
          <div className="ef3d-label ef3d-label-right">
            <span className="ef3d-label-title">{t('emPowerFlowLoad')}</span>
            <span className="ef3d-label-value ef3d-val-house">{load}</span>
          </div>
        </div>

        {/* Grid */}
        <div className="ef3d-node ef3d-node-bottom">
          <div className="ef3d-icon-wrap ef3d-icon-grid">
            <div className="ef3d-icon-inner">
              <GridIcon />
            </div>
            <div className="ef3d-icon-glow ef3d-glow-grid" />
          </div>
          <div className="ef3d-label ef3d-label-bottom">
            <span className="ef3d-label-title">{gridLabel}</span>
            <span className="ef3d-label-value ef3d-val-grid">{gridPower}</span>
          </div>
        </div>

        <Lane className={`ef3d-lane ef3d-lane-top ${flows.solar ? 'is-active' : ''}`} particles={particles} />
        <Lane className={`ef3d-lane ef3d-lane-left ${flows.battery ? 'is-active' : ''}`} particles={particles} />
        <Lane className={`ef3d-lane ef3d-lane-right ${flows.load ? 'is-active' : ''}`} particles={particles} />
        <Lane className={`ef3d-lane ef3d-lane-bottom ${flows.grid ? 'is-active' : ''}`} particles={particles} />
      </div>
    </div>
  );
}

function Lane({ className, particles }) {
  return (
    <div className={className}>
      <span className="ef3d-track" />
      {particles.map((i) => (
        <span key={i} className="ef3d-particle" style={{ animationDelay: `${i * 0.45}s` }} />
      ))}
    </div>
  );
}

function SolarIcon() {
  return (
    <svg viewBox="0 0 64 64" width="100%" height="100%">
      <defs>
        <linearGradient id="solarG" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#fff" />
          <stop offset="100%" stopColor="#cfe6ff" />
        </linearGradient>
      </defs>
      <g stroke="url(#solarG)" strokeWidth="2.2" strokeLinecap="round">
        <line x1="32" y1="5" x2="32" y2="11" />
        <line x1="14" y1="10" x2="18" y2="14" />
        <line x1="50" y1="10" x2="46" y2="14" />
        <line x1="6" y1="22" x2="12" y2="22" />
        <line x1="58" y1="22" x2="52" y2="22" />
      </g>
      <g transform="translate(10 20) skewX(-12)">
        <rect x="0" y="0" width="44" height="32" rx="2" fill="none" stroke="url(#solarG)" strokeWidth="2" />
        <line x1="0" y1="11" x2="44" y2="11" stroke="url(#solarG)" strokeWidth="1.4" />
        <line x1="0" y1="22" x2="44" y2="22" stroke="url(#solarG)" strokeWidth="1.4" />
        <line x1="15" y1="0" x2="15" y2="32" stroke="url(#solarG)" strokeWidth="1.4" />
        <line x1="30" y1="0" x2="30" y2="32" stroke="url(#solarG)" strokeWidth="1.4" />
      </g>
    </svg>
  );
}

function BatteryIcon() {
  return (
    <svg viewBox="0 0 64 64" width="100%" height="100%">
      <rect x="18" y="10" width="28" height="44" rx="4" fill="none" stroke="#eaf3ff" strokeWidth="2.4" />
      <rect x="26" y="6" width="12" height="5" rx="1.5" fill="#eaf3ff" />
      <path d="M33 20 L24 36 H32 L30 46 L41 28 H33 Z" fill="#eaf3ff" />
    </svg>
  );
}

function HouseIcon() {
  return (
    <svg viewBox="0 0 64 64" width="100%" height="100%">
      <path
        d="M12 30 L32 10 L52 30 V54 H12 Z"
        fill="none"
        stroke="#eaf3ff"
        strokeWidth="2.4"
        strokeLinejoin="round"
      />
      <path d="M34 26 L24 42 H31 L29 52 L40 34 H33 Z" fill="#eaf3ff" />
    </svg>
  );
}

function GridIcon() {
  return (
    <svg viewBox="0 0 64 64" width="100%" height="100%">
      <g fill="none" stroke="#eaf3ff" strokeWidth="2.2" strokeLinecap="round">
        <path d="M32 6 L14 58" />
        <path d="M32 6 L50 58" />
        <path d="M22 30 L42 30" />
        <path d="M18 42 L46 42" />
        <path d="M26 18 L38 18" />
        <path d="M14 58 L50 58" />
      </g>
    </svg>
  );
}

function InverterIcon() {
  return (
    <svg viewBox="0 0 64 64" width="100%" height="100%">
      <rect x="14" y="18" width="36" height="28" rx="5" fill="none" stroke="#dff1ff" strokeWidth="2.4" />
      <path
        d="M20 34 Q26 24 32 34 T44 34"
        fill="none"
        stroke="#dff1ff"
        strokeWidth="2.2"
        strokeLinecap="round"
      />
      <circle cx="21" cy="42" r="1.6" fill="#dff1ff" />
      <circle cx="27" cy="42" r="1.6" fill="#dff1ff" />
    </svg>
  );
}
